import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type {
  ClaireLiveQualityReport,
  ClaireRegressionRunsReport,
} from '@borradh-workspace/api-client/types';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import {
  useClaireLiveQuality,
  useRefreshClaireLiveQuality,
} from '../../api/claire-live-quality';
import {
  useClaireRegressions,
  useRefreshClaireRegressions,
} from '../../api/claire-regressions';
import { FixesSection } from './fixes-section';
import { MistakesSection } from './mistakes-section';
import { OutcomesSection } from './outcomes-section';
import { RunSimulationSlot } from './run-simulation-slot';
import { TestRunsSection } from './test-runs-section';
import { type ExplorerRequest, TurnExplorer } from './turn-explorer';

function Failed({ what, message }: { what: string; message?: string }) {
  return (
    <Alert variant="destructive">
      <AlertTitle>Could not load {what}</AlertTitle>
      <AlertDescription>{message ?? 'The request failed.'}</AlertDescription>
    </Alert>
  );
}

/**
 * The whole page from its two reports, so tests can render it without the
 * network. Either report may still be loading (undefined).
 */
export function ClaireQualityView({
  live,
  runs,
  onOpen,
}: {
  live?: ClaireLiveQualityReport;
  runs?: ClaireRegressionRunsReport;
  onOpen: (r: ExplorerRequest) => void;
}) {
  return (
    <div className="flex flex-col gap-12">
      {live ? (
        live.configured ? (
          <>
            <OutcomesSection report={live} onOpen={onOpen} />
            <MistakesSection report={live} onOpen={onOpen} />
            <FixesSection report={live} runs={runs?.runs} />
          </>
        ) : (
          <Alert>
            <AlertTitle>Langfuse is not connected on this API</AlertTitle>
            <AlertDescription>
              The live numbers come from the production Langfuse project. Set
              CLAIRE_REGRESSION_LANGFUSE_PUBLIC_KEY and
              CLAIRE_REGRESSION_LANGFUSE_SECRET_KEY on the API.
            </AlertDescription>
          </Alert>
        )
      ) : null}
      <section className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="text-2xl font-semibold">Test runs</h2>
          <RunSimulationSlot />
        </div>
        {runs ? (
          <TestRunsSection report={runs} onOpen={onOpen} />
        ) : (
          <Skeleton className="h-48 w-full" />
        )}
      </section>
    </div>
  );
}

export function ClaireRegressionsPage() {
  const live = useClaireLiveQuality();
  const runs = useClaireRegressions();
  const refreshLive = useRefreshClaireLiveQuality();
  const refreshRuns = useRefreshClaireRegressions();
  const [explorer, setExplorer] = useState<ExplorerRequest | null>(null);
  const refreshing = refreshLive.isRefreshing || refreshRuns.isRefreshing;

  return (
    <div className="container max-w-6xl py-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <h1 className="text-3xl font-bold">How is Claire doing?</h1>
          <p className="text-muted-foreground">
            Claire answers clinics&apos; customers on SMS, WhatsApp, Messenger
            and Instagram. An AI judge sorts every customer message she answers
            into one of three outcomes. Click any number to read the
            conversations behind it.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={refreshing}
          onClick={() => {
            refreshLive.refresh();
            refreshRuns.refresh();
          }}
        >
          <RefreshCw className={cn(refreshing && 'animate-spin')} />
          Refresh
        </Button>
      </div>
      {live.isLoading ? (
        <div className="mb-12 grid gap-4 md:grid-cols-3">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      ) : live.isError ? (
        <div className="mb-12">
          <Failed what="Claire's live numbers" message={live.error?.message} />
        </div>
      ) : null}
      {runs.isError ? (
        <Failed what="the test runs" message={runs.error?.message} />
      ) : null}
      <ClaireQualityView
        live={live.data}
        runs={runs.isError ? undefined : runs.data}
        onOpen={setExplorer}
      />
      <TurnExplorer
        request={explorer}
        onChange={setExplorer}
        onClose={() => setExplorer(null)}
      />
    </div>
  );
}
